/**
 * Master list of permissions. The software defines what is possible;
 * Settings → Roles decides which roles get which permissions.
 *
 * Add new permissions here as modules are built. Never rename a key once
 * it is in use: keys are stored in the database.
 */

export interface PermissionDef {
  key: string;
  label: string;
  description: string;
  group: PermissionGroup;
  /** Hidden from the role editor until the module that uses it ships. */
  available: boolean;
}

/** Every report's permission; the Reports page shows to anyone with one. */
export const REPORT_PERMISSIONS = ["reports.overview", "reports.leaderboard", "reports.satisfaction", "reports.closeRate", "reports.aging", "reports.pipeline", "reports.leadSources", "reports.jobProfit", "reports.qbPnl"] as const;

export type PermissionGroup = "Contacts and jobs" | "Money" | "Reports" | "Administration";

export const PERMISSION_GROUPS: PermissionGroup[] = ["Contacts and jobs", "Money", "Reports", "Administration"];

const defs = [
  // Contacts and jobs (keys predate the customer/contact merge; never rename them)
  {
    key: "customers.view",
    label: "View contacts and jobs",
    description: "See contacts (customers, subcontractors, vendors...), jobs, and their notes.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "customers.edit",
    label: "Add and edit contacts",
    description: "Create and change contacts and their related contacts.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "jobs.edit",
    label: "Add and edit jobs",
    description: "Create jobs, change their details, and move them between stages.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "notes.add",
    label: "Add notes",
    description: "Write notes on contacts and jobs. People can always edit their own notes.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "files.upload",
    label: "Add files and photos",
    description: "Upload files and photos to jobs and contacts, and change their category, tags, and captions. People can always delete their own uploads.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "appointments.edit",
    label: "Schedule appointments",
    description: "Add, change, and cancel appointments on the calendar, for anyone.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "tasks.manage_all",
    label: "Manage everyone's tasks",
    description: "See everyone's tasks and assign tasks to other people. Without it, people see and add only their own.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "workorders.edit",
    label: "Make and send work orders",
    description: "Write work orders for subcontractors and crews, send them, and mark them done. Prices show only with \"See job money\".",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "materials.order",
    label: "Measurements and material orders",
    description: "Upload roof measurement reports and make, send, and track material orders.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "permits.edit",
    label: "Manage permits and warranties",
    description: "Add permits and inspections, and change warranties and warranty claims.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "comms.view",
    label: "See texts and calls",
    description: "See texts, calls, and voicemails with contacts, and the communications inbox.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "comms.view_all",
    label: "See all lines",
    description: "See texts, calls, voicemails, and faxes on every line: the main number, the fax, and everyone's extensions. Without it, people see only their own extension's.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "comms.send",
    label: "Send texts",
    description: "Text contacts from the CRM (from the company number).",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "records.delete",
    label: "Delete and restore records",
    description: "Move contacts and jobs to the recycle bin, and restore them.",
    group: "Contacts and jobs",
    available: true,
  },

  // Money (modules ship in later phases)
  {
    key: "money.view",
    label: "See job money",
    description: "See contract amounts, payments, and balances on jobs.",
    group: "Money",
    available: true,
  },
  {
    key: "proposals.edit",
    label: "Make and send proposals",
    description: "Read carrier estimates, build proposals, and send them to customers to sign. Needs \"See job money\" too.",
    group: "Money",
    available: true,
  },
  {
    key: "invoices.edit",
    label: "Make invoices and record payments",
    description: "Create and send invoices on jobs, and record checks, cash, and financing. Needs \"See job money\" too.",
    group: "Money",
    available: true,
  },
  {
    key: "receipts.submit",
    label: "Turn in receipts",
    description: "Upload receipts and bills, check what was read, and mark them ready. People see the receipts they turned in; with \"See job money\" they see everyone's.",
    group: "Money",
    available: true,
  },
  {
    key: "receipts.approve",
    label: "Approve receipts and bills",
    description: "Approve receipts and bills marked ready (they go to QuickBooks once that's connected), and send them back for changes.",
    group: "Money",
    available: true,
  },
  {
    key: "quickbooks.push",
    label: "Send to QuickBooks",
    description: "Approve bills and expenses and send records to QuickBooks.",
    group: "Money",
    available: false,
  },

  // Reports: one permission each, so any report can be given to a role or a person on its own.
  {
    key: "reports.overview",
    label: "Business overview",
    description: "Sales, collected, new jobs, money owed, and depreciation to collect, compared with last year.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.leaderboard",
    label: "Rep leaderboard",
    description: "Each rep's sales and collections.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.satisfaction",
    label: "Customer satisfaction",
    description: "\"How did we do?\" survey results.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.closeRate",
    label: "Proposal close rate",
    description: "Proposals sent, signed, and lost.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.aging",
    label: "Money owed (aging)",
    description: "Unpaid invoice balances by days past due.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.pipeline",
    label: "Pipeline",
    description: "Jobs in each stage, with signed contract value.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.leadSources",
    label: "Lead sources",
    description: "Jobs added by lead source, and how many sold.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.jobProfit",
    label: "Job profit",
    description: "The CRM's contract and collections against costs, across jobs.",
    group: "Reports",
    available: true,
  },
  {
    key: "reports.qbPnl",
    label: "Job P&L from QuickBooks",
    description: "A job's income and expenses from QuickBooks, with overhead and net profit (the P&L tab on a job).",
    group: "Reports",
    available: true,
  },

  // Administration
  {
    key: "users.manage",
    label: "Manage users",
    description: "Add, edit, and deactivate users, assign their roles, and end their sessions.",
    group: "Administration",
    available: true,
  },
  {
    key: "roles.manage",
    label: "Manage roles and permissions",
    description: "Create and edit roles, and override permissions for individual users.",
    group: "Administration",
    available: true,
  },
  {
    key: "settings.manage",
    label: "Manage settings",
    description: "Change company details, theme, lists, contact types, workflows, and security settings.",
    group: "Administration",
    available: true,
  },
  {
    key: "taxid.view",
    label: "See tax IDs",
    description: "See subcontractors' full tax IDs (SSN or EIN). Everyone else sees the last four digits.",
    group: "Administration",
    available: true,
  },
  {
    key: "import.run",
    label: "Import data",
    description: "Pull records from JobNimbus, review them, and add them to the CRM.",
    group: "Administration",
    available: true,
  },
  {
    key: "fred.use",
    label: "Use Fred, the assistant",
    description: "Ask Fred to find things and make changes. Fred can only do what the person can do, and asks before changing anything.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "notifications.team",
    label: "Notifications for my team's jobs",
    description: "Get notifications (proposals signed, payments, replies...) on the jobs of the people who report to them, and their people's, as they choose in their notifications.",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "notifications.everyone",
    label: "Notifications for everyone's jobs",
    description: "Get notifications on any job in the company, as they choose in their notifications (for owners and managers over everyone).",
    group: "Contacts and jobs",
    available: true,
  },
  {
    key: "audit.view",
    label: "View activity log",
    description: "See the record of who did what in the system.",
    group: "Administration",
    available: true,
  },
] as const satisfies readonly PermissionDef[];

export type PermissionKey = (typeof defs)[number]["key"];

export const PERMISSIONS: readonly PermissionDef[] = defs;

export const PERMISSION_KEYS = new Set<string>(defs.map((d) => d.key));

export function isPermissionKey(key: string): key is PermissionKey {
  return PERMISSION_KEYS.has(key);
}

/**
 * Effective permissions = union of role permissions,
 * plus per-person grants, minus per-person revokes.
 */
export function effectivePermissions(
  rolePermissions: Iterable<string>,
  overrides: Iterable<{ permission: string; effect: "grant" | "revoke" }>,
): Set<PermissionKey> {
  const result = new Set<string>();
  for (const p of rolePermissions) if (isPermissionKey(p)) result.add(p);
  const list = [...overrides];
  for (const o of list) if (o.effect === "grant" && isPermissionKey(o.permission)) result.add(o.permission);
  for (const o of list) if (o.effect === "revoke") result.delete(o.permission);
  return result as Set<PermissionKey>;
}

/** Starting roles. Edit them freely in Settings → Roles. */
export const DEFAULT_ROLES: { name: string; description: string; permissions: PermissionKey[] }[] = [
  {
    name: "Admin",
    description: "Full access, including settings, users, roles, and money.",
    permissions: defs.map((d) => d.key),
  },
  {
    name: "Sales",
    description: "Contacts and jobs, including job money.",
    permissions: ["customers.view", "customers.edit", "jobs.edit", "notes.add", "files.upload", "appointments.edit", "money.view", "proposals.edit", "workorders.edit", "comms.view", "comms.send", "receipts.submit", "invoices.edit", "permits.edit", "materials.order", "fred.use"],
  },
  {
    name: "Field",
    description: "Sees contacts and jobs and adds notes and photos; can't change records.",
    permissions: ["customers.view", "notes.add", "files.upload", "comms.view", "receipts.submit", "fred.use"],
  },
];
